"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RaiseEvent = exports.ListenerManager = void 0;
var RaiseEvent;
(function (RaiseEvent) {
    RaiseEvent["ALWAYS"] = "always";
    RaiseEvent["ONCE"] = "once";
    RaiseEvent["NEVER"] = "never";
})(RaiseEvent || (RaiseEvent = {}));
exports.RaiseEvent = RaiseEvent;
class Listener {
    constructor(configs) {
        var _a;
        this.eventName = configs.eventName;
        this.context = configs.context;
        this.enabled = (_a = configs.enabled) !== null && _a !== void 0 ? _a : true;
        this.binderName = configs.binderName || 'addEventListener';
        this.unbinderName = configs.unbinderName || 'removeEventListener';
        this.binder = this.context[this.binderName].bind(this.context);
        this.unbinder = this.context[this.unbinderName].bind(this.context);
    }
    addHandler(handler) {
        if (!this.enabled || !handler)
            return;
        const name = this.context === window || this.context === document ? this.eventName : null;
        name ? this.binder(name, handler) : this.binder(handler);
    }
    removeHandler(handler) {
        if (!this.enabled || !handler)
            return;
        const name = this.context === window || this.context === document ? this.eventName : null;
        name ? this.unbinder(name, handler) : this.unbinder(handler);
    }
}
class ListenerManager {
    constructor(eventManager, configs, eventTracker) {
        this.handlers = {};
        this.listeners = {};
        this.eventManager = eventManager;
        this.eventTracker = eventTracker;
        this.raisedEvents = [];
        this.setup(configs);
    }
    createListeners() {
        this.listeners.userLoaded = new Listener({
            eventName: 'userLoaded',
            context: this.eventManager,
            binderName: 'addUserLoaded',
            unbinderName: 'removeUserLoaded',
        });
        this.listeners.userUnloaded = new Listener({
            eventName: 'userUnloaded',
            context: this.eventManager,
            binderName: 'addUserUnloaded',
            unbinderName: 'removeUserUnloaded',
        });
        this.listeners.silentRenewError = new Listener({
            eventName: 'silentRenewError',
            context: this.eventManager,
            enabled: this.configs.automaticSilentRenew,
            binderName: 'addSilentRenewError',
            unbinderName: 'removeSilentRenewError',
        });
        this.listeners.userSignedIn = new Listener({
            eventName: 'userSignedIn',
            context: this.eventManager,
            binderName: 'addUserSignedIn',
            unbinderName: 'removeUserSignedIn',
        });
        this.listeners.userSignedOut = new Listener({
            eventName: 'userSignedOut',
            context: this.eventManager,
            binderName: 'addUserSignedIn',
            unbinderName: 'removeUserSignedOut',
        });
        this.listeners.userSessionChanged = new Listener({
            eventName: 'userSessionChanged',
            context: this.eventManager,
            binderName: 'addUserSessionChanged',
            unbinderName: 'removeUserSessionChanged',
        });
        this.listeners.accessTokenExpiring = new Listener({
            eventName: 'accessTokenExpiring',
            context: this.eventManager,
            binderName: 'addAccessTokenExpiring',
            unbinderName: 'removeAccessTokenExpiring'
        });
        this.listeners.accessTokenExpired = new Listener({
            eventName: 'accessTokenExpired',
            context: this.eventManager,
            binderName: 'addAccessTokenExpired',
            unbinderName: 'removeAccessTokenExpired',
        });
        this.listeners.online = new Listener({
            eventName: 'online',
            context: window,
            enabled: this.configs.revalidateOnReconnect,
        });
        this.listeners.focus = new Listener({
            eventName: 'visibilitychange',
            context: document,
            enabled: this.configs.revalidateOnFocus,
        });
    }
    setup(configs) {
        this.configs = configs;
        this.createListeners();
    }
    /**
     * @description Starts the events available in createListeners.
     * @param newConfigs (Optional) New settings to be applied before starting.
     */
    start(newConfigs) {
        if (newConfigs) {
            this.stop();
            this.setup(newConfigs);
        }
        Object.entries(this.handlers).forEach(([name, handler]) => {
            this.listeners[name].addHandler(handler);
        });
    }
    /**
     * @description Removes all registered listeners.
     */
    stop() {
        Object.entries(this.handlers).forEach(([name, handler]) => {
            this.listeners[name].removeHandler(handler);
        });
    }
    /**
     * @description Registers a handler for a given event.
     * @param name Name of the event with which the handler will be associated.
     * @param handler Function that will be executed when the event is triggered.
     * @param raiseEvent Boolean ...
     */
    setHandler(name, handler, raiseEvent = RaiseEvent.ALWAYS) {
        this.handlers[name] = (args) => {
            handler(args);
            if (name === 'userLoaded')
                this.raisedEvents = [];
            this.sendEvent(name, raiseEvent, args === null || args === void 0 ? void 0 : args.message);
        };
    }
    /**
     * @description Send event to tracker agent.
     * @param name Name of the event with which the handler will be associated.
     * @param raiseEvent Frequency at which the event should be sent.
     * @param [detail] Event detail, used for explain the cause of an error.
     */
    sendEvent(name, raiseEvent, detail) {
        if (raiseEvent === RaiseEvent.NEVER)
            return;
        const normalizedName = name.split(/(?=[A-Z])/).join('_').toLowerCase();
        if (raiseEvent === RaiseEvent.ONCE) {
            if (this.raisedEvents.find((event) => event === name))
                return;
            this.raisedEvents.push(name);
        }
        this.eventTracker.send(normalizedName, { detail });
    }
}
exports.ListenerManager = ListenerManager;
